import React from 'react';
import { Warning } from '@phosphor-icons/react';
import HelpApiLayout from '../../../../components/layout/HelpApiLayout';
import HelpSidebar from '../../../../components/layout/HelpSidebar';
import ArticleNavigation from '../../../../components/layout/docs/ArticleNavigation';
import LastUpdated from '../../../../components/layout/docs/LastUpdated';
import { getHelpArticleRoute } from '../../../../constants/routes';
import { getHelpRoute } from '../../../../utils/navigation';
import '../getting-started/HelpCategoryPage.css';

/**
 * Link Cards & Wallets Article Page
 */
const LinkCardsWallets = () => {
  return (
    <HelpApiLayout type="help" sidebar={<HelpSidebar />}>
      <div className="help-category-page">
        <div className="help-category-container">
          <main className="help-category-main">
            <div className="article-content">
              <header className="article-header">
                <h1 className="article-title">Link Cards & Wallets</h1>
                <p className="article-subtitle">
                  Link external wallets or crypto-debit cards to your WeSplit account to streamline withdrawals and track external balances in one place.
                </p>
              </header>

              <div className="article-separator"></div>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Overview</h2>
                  <p className="article-section-intro">
                    You can save external addresses, such as personal hardware wallets or crypto-backed debit cards, to your WeSplit profile. This allows setting them as default destinations for withdrawals and, in some cases, viewing current balances directly within the app.
                  </p>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">How to Link a New Account</h2>
                </div>

                <div className="article-subsection">
                  <ol className="article-list">
                    <li>
                      <strong>Access Wallet Settings:</strong> Go to the main Wallets page within the app.
                    </li>
                    <li>
                      <strong>Initiate Link:</strong> Tap on the "Link wallets & card" button.
                    </li>
                    <li>
                      <strong>Enter Details:</strong>
                      <ul className="article-list-dots">
                        <li>
                          <strong>Name:</strong> Give the link a recognizable name (e.g., "My Ledger," "Crypto Visa," "Vault").
                        </li>
                        <li>
                          <strong>Address:</strong> Paste the Public Wallet Address.
                        </li>
                        <li>
                          <strong>Type:</strong> Specify if this is a Wallet or a Card.
                        </li>
                      </ul>
                    </li>
                  </ol>
                </div>
              </section>

              <section className="article-section">
                <div className="article-subsection">
                  <h2 className="article-section-title">Configuration Options</h2>
                </div>

                <div className="article-subsection">
                  <ul className="article-list-dots">
                    <li>
                      <strong>Display Balance:</strong> A toggle option to fetch and show the current balance of the external address on the dashboard.
                      <div className="article-note" style={{ marginTop: '12px' }}>
                        <p>Note: Some wallet providers or privacy settings may block us from reading the balance.</p>
                      </div>
                    </li>
                    <li>
                      <strong>Main Withdraw Point:</strong> A toggle option to set this address as the default destination for withdrawals from a group or personal wallet, meaning it will be pre-selected.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="article-section">
                <div className="article-warning">
                  <div className="article-warning-header">
                    <Warning className="article-warning-icon" weight="fill" />
                    <h3 className="article-warning-title">Solana Network Only</h3>
                  </div>
                  <div className="article-warning-content">
                    <p>
                      WeSplit operates exclusively on the Solana blockchain.
                    </p>
                    <ul className="article-list-dots">
                      <li>
                        Any wallet linked must be a Solana-compatible wallet.
                      </li>
                      <li>
                        <strong>Crypto Cards:</strong> Only debit cards that provide a specific Solana wallet address for funding (top-ups) can be linked. If a card provider only gives an IBAN or a Bitcoin address, it cannot be linked here.
                      </li>
                    </ul>
                  </div>
                </div>
              </section>

              <div className="article-separator"></div>

              <LastUpdated />

              <ArticleNavigation
                next={{
                  title: 'Your Profile',
                  route: getHelpRoute(getHelpArticleRoute('features', 'your-profile'))
                }}
              />
            </div>
          </main>
        </div>
      </div>
    </HelpApiLayout>
  );
};

export default LinkCardsWallets;
